import { type ProgramData, type ProgramId, type ProgramUserData, type UserId, sortPrograms } from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { sortProgramTasks } from './util'
import {
  programKey,
  programListKey,
  programStatsKey,
  programUserInfiniteKey,
  programUserKey,
  programUsersKey,
} from './keys'

type ProgramCache = {
  clearItem: (programId: ProgramId) => void
  clearList: () => void
  clearUsers: (programId: ProgramId) => void
  clearStats: () => void
  setItem: (program: ProgramData) => void
  addItem: (program: ProgramData) => void
  setUser: (user: ProgramUserData) => void
  removeUser: (programId: ProgramId, userId: UserId) => void
}

export function useProgramCache(): ProgramCache {
  const queryClient = useQueryClient()
  return {
    clearItem: (programId: ProgramId) => {
      queryClient.invalidateQueries({ queryKey: programKey(programId) })
    },
    clearList: () => {
      queryClient.invalidateQueries({ queryKey: programListKey() })
    },
    clearUsers: (programId: ProgramId) => {
      queryClient.invalidateQueries({ queryKey: programUsersKey(programId) })
      queryClient.invalidateQueries({ queryKey: programUserKey(programId) })
      queryClient.invalidateQueries({ queryKey: programUserInfiniteKey(programId) })
    },
    clearStats: () => {
      queryClient.invalidateQueries({ queryKey: programStatsKey() })
    },
    setItem: (program: ProgramData) => {
      queryClient.setQueryData(programKey(program.programId), sortProgramTasks(program))
      queryClient.setQueriesData({ queryKey: programListKey() }, (old: ProgramData[]) => {
        if (!old) return
        return sortPrograms(old.map((p) => (p.programId === program.programId ? sortProgramTasks(program) : p)))
      })
    },
    addItem: (program: ProgramData) => {
      queryClient.setQueryData(programKey(program.programId), program)
      queryClient.setQueriesData({ queryKey: programListKey() }, (old: ProgramData[]) => {
        if (!old) return
        return sortPrograms([...old, sortProgramTasks(program)])
      })
    },
    setUser: (user: ProgramUserData) => {
      queryClient.setQueriesData({ queryKey: programUsersKey(user.programId) }, (old: ProgramUserData[]) => {
        if (!old) return
        return old.map((u) => (u.userId === user.userId ? user : u))
      })
      queryClient.setQueriesData({ queryKey: programUserInfiniteKey(user.programId) }, (old: ProgramUserData[]) => {
        if (!old) return
        return old.map((u) => (u.userId === user.userId ? user : u))
      })
      queryClient.setQueryData(programUserKey(user.programId, user.userId), user)
    },
    removeUser: (programId: ProgramId, userId: UserId) => {
      queryClient.setQueriesData({ queryKey: programUserKey(programId) }, (old: ProgramUserData[]) => {
        if (!old) return
        return old.filter((u) => u.userId !== userId)
      })
      queryClient.setQueriesData({ queryKey: programUserInfiniteKey(programId) }, (old: ProgramUserData[]) => {
        if (!old) return
        return old.filter((u) => u.userId !== userId)
      })
    },
  }
}
